import React, { useState, useEffect, useRef } from 'react';
import { WebsiteDesignProps } from './types';
import { 
  Shield, Book, Award, Phone, ArrowRight, ArrowUpRight, 
  Menu, X, Zap, Heart, Users, Trophy, School, Clock, 
  MapPin, Mail, Globe, GraduationCap, ChevronLeft, 
  ChevronRight, ChevronDown, Download, Send, Plus, Layout, Sparkles, 
  FileText, Instagram, Facebook, Linkedin, Youtube, Quote, BookOpen, Star, Camera, CheckCircle
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Button } from "@/components/ui/button";
import { VisualImageEditable } from "@/components/VisualImageEditable";
import { VisualTextEditable } from "@/components/VisualTextEditable";

const InternationalStandard: React.FC<WebsiteDesignProps> = ({ previewData, isEditMode, onUpdateField }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLoginDropdown, setShowLoginDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Icon resolver helper
  const getIcon = (name: string | any) => {
      if (!name) return Shield;
      if (typeof name !== 'string') return name;
      // @ts-ignore
      const Icon = LucideIcons[name];
      return Icon || Shield;
  };

  const schoolName = previewData?.name || "Global International School";

  // Shared Data Model (Strict Matching Design 1)
  const defaultData = {
    hero: {
      images: ["https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=2070&auto=format&fit=crop"],
      tagline: "Empowering Students for a Changing World",
      titlePrefix: "World-Class",
      subtitle: "Join a community of cross-continental learners where integrity meets excellence in every classroom."
    },
    features: [
      { icon: "CheckCircle", title: "Dual Curriculum", desc: "Offering both Cambridge IGCSE and National standards." },
      { icon: "Globe", title: "Global Network", desc: "Exchange programs with sister schools in Switzerland and Singapore." },
      { icon: "Award", title: "Merit Scholars", desc: "Over $500k in university scholarships awarded annually." }
    ],
    programs: [
      { title: "Primary Foundations", age: "Years 1-6", desc: "Core literacy and numeracy with a focus on creative arts.", img: "https://images.unsplash.com/photo-1491841573634-28140fc7ced7?q=80&w=2070&auto=format&fit=crop" },
      { title: "Diploma Programme", age: "Years 12-13", desc: "Intensive pre-university training for top global institutions.", img: "https://images.unsplash.com/photo-1543269865-cbf427effbad?q=80&w=2070&auto=format&fit=crop" }
    ],
    stats: [
        { value: "45+", label: "NATIONALITIES" },
        { value: "100%", label: "GRADUATION RATE" },
        { value: "12:1", label: "STUDENT RATIO" }
    ],
    news: [
        { title: "Science Fair 2026", date: "FEB 12", desc: "Winners announced for the regional clean energy project.", img: "https://images.unsplash.com/photo-1564066399954-ac473ca5078d?q=80&w=2070&auto=format&fit=crop" }
    ],
    nav: [
        { label: 'ABOUT', href: '#about' },
        { label: 'ACADEMICS', href: '#programs' },
        { label: 'ADMISSIONS', href: '#cta' },
        { label: 'CONTACT', href: '#contact' }
    ],
    team: [
        { name: "Dr. Arthur Bell", role: "Head of School", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1974&auto=format&fit=crop" },
        { name: "Mrs. Sarah Jenkins", role: "Academic Registrar", img: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=1976&auto=format&fit=crop" }
    ],
    gallery: [
        { category: "Campus", img: "https://images.unsplash.com/photo-1525921429624-479b6a29d84c?q=80&w=2070&auto=format&fit=crop", title: "Assembly Square" },
        { category: "Athletics", img: "https://images.unsplash.com/photo-1546519638-68e109498ffc?q=80&w=2090&auto=format&fit=crop", title: "Indoor Courts" }
    ],
    facilities: [
        { title: "Auditorium", desc: "600-seat professional theater for performing arts.", img: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=2070&auto=format&fit=crop", icon: "Mic" },
        { title: "Science Center", desc: "Equipped with advanced biology and chemistry labs.", img: "https://images.unsplash.com/photo-1576086213369-97a306d36557?q=80&w=2080&auto=format&fit=crop", icon: "Zap" }
    ]
  };

  const mockData = {
      ...previewData,
      hero: {
          titlePrefix: previewData?.hero?.titlePrefix || defaultData.hero.titlePrefix,
          tagline: previewData?.hero?.tagline || defaultData.hero.tagline,
          subtitle: previewData?.hero?.subtitle || defaultData.hero.subtitle,
          images: previewData?.hero?.images || defaultData.hero.images,
          backgroundImage: previewData?.hero?.backgroundImage || defaultData.hero.images[0]
      },
      about: {
          title: previewData?.about?.title || "Excellence. Integrity. Service.",
          description: previewData?.about?.description || "For over 25 years, " + schoolName + " has been at the forefront of international education, fostering a culture of curiosity and respect. Our graduates are leaders who embody the values of global citizenship.",
          sectionImage: previewData?.about?.sectionImage || "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=2070&auto=format&fit=crop"
      },
      features: (previewData?.features || defaultData.features).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || ""
      })),
      programs: (previewData?.programs || defaultData.programs).map((p: any) => ({
        title: p.name || p.title || "",
        age: p.age || "",
        desc: p.description || p.desc || "",
        img: p.image || p.img || ""
      })),
      stats: (previewData?.stats || defaultData.stats).map((s: any) => ({
          value: s.value || "0",
          label: s.label || ""
      })),
      news: (previewData?.news || defaultData.news).map((n: any) => ({
        title: n.title || "",
        date: n.date || "",
        desc: n.description || n.desc || "",
        img: n.image || n.img || ""
      })),
      team: (previewData?.team || defaultData.team).map((t: any) => ({
        name: t.name || "",
        role: t.role || "",
        img: t.img || t.image || "",
        linkedin: t.linkedin || "",
        email: t.email || ""
      })),
      gallery: (previewData?.gallery || defaultData.gallery).map((g: any) => ({
        category: g.category || "General",
        title: g.caption || g.title || "",
        img: g.image || g.img || ""
      })),
      facilities: (previewData?.facilities || defaultData.facilities).map((f: any) => ({ 
        ...f, 
        icon: getIcon(f.icon),
        desc: f.description || f.desc || "",
        img: f.image || f.img || ""
      })),
      downloads: (previewData?.downloads || [
          { title: "Strategic_Plan_2030.pdf", size: "1.2 MB", type: "PDF" },
          { title: "Student_Handbook.pdf", size: "4.5 MB", type: "PDF" }
      ]).map((d: any) => ({
          title: d.title || "",
          size: d.size || "",
          type: d.type || "PDF"
      })),
      contact: {
        address: previewData?.contact?.address || "77 Global Way, Upper Hill, Nairobi",
        phone: previewData?.contact?.phone || "+254 750 222 333",
        email: previewData?.contact?.email || "office@globalint.edu",
        officeHours: previewData?.contact?.officeHours || "Mon - Fri: 8:00 AM - 4:30 PM",
        facebook: previewData?.contact?.facebook || "",
        instagram: previewData?.contact?.instagram || "",
        youtube: previewData?.contact?.youtube || "",
        linkedin: previewData?.contact?.linkedin || ""
      },
      sectionVisibility: {
          hero: previewData?.sectionVisibility?.hero !== false,
          about: previewData?.sectionVisibility?.about !== false,
          stats: previewData?.sectionVisibility?.stats !== false,
          features: previewData?.sectionVisibility?.features !== false,
          programs: previewData?.sectionVisibility?.programs !== false,
          facilities: previewData?.sectionVisibility?.facilities !== false,
          team: previewData?.sectionVisibility?.team !== false,
          gallery: previewData?.sectionVisibility?.gallery !== false,
          news: previewData?.sectionVisibility?.news !== false,
          downloads: previewData?.sectionVisibility?.downloads !== false,
          cta: previewData?.sectionVisibility?.cta !== false,
          contact: previewData?.sectionVisibility?.contact !== false
      },
      nav: (previewData?.nav || defaultData.nav).map((n: any) => ({
          label: n.label || "",
          href: n.href || "#",
          visible: n.visible !== false
      })),
      labels: {
          downloadsSection: previewData?.labels?.downloadsSection || "Official Documentation",
          downloadsTitle: previewData?.labels?.downloadsTitle || "Institutional Archives",
          contactFormTitle: previewData?.labels?.contactFormTitle || "Contact Form",
          contactFormSubtitle: previewData?.labels?.contactFormSubtitle || "Fill out the form below."
      }
  };

  useEffect(() => {
    const doc = containerRef.current?.ownerDocument || document;
    const root = doc.documentElement;
    if (previewData?.theme) {
        const { colors, fonts } = previewData.theme;
        root.style.setProperty('--color-primary', colors.primary || '#0056b3');
        root.style.setProperty('--color-secondary', colors.secondary || '#e5e7eb');
        root.style.setProperty('--color-accent', colors.accent || '#ffd700');
        root.style.setProperty('--color-background', colors.background || '#ffffff');
        root.style.setProperty('--color-text', colors.text || '#374151');
        root.style.setProperty('--font-heading', `'${fonts.heading || 'Playfair Display'}', serif`);
        root.style.setProperty('--font-body', `'${fonts.body || 'Inter'}', sans-serif`);
    }
    const fontLink = doc.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Inter:wght@300;400;500;600;700&display=swap";
    fontLink.rel = 'stylesheet';
    doc.head.appendChild(fontLink);
  }, [previewData?.theme]);

  const handleUpdate = (path: string, val: any) => onUpdateField && onUpdateField(path, val);
  const addArrayItem = (path: string, newItem: any) => {
    if (onUpdateField) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, [...arr, newItem]);
    }
  };
  const removeArrayItem = (path: string, index: number) => {
    if (onUpdateField && window.confirm('Delete this record?')) {
        const arr = [...((mockData as any)[path] || [])];
        onUpdateField(path, arr.filter((_, i) => i !== index));
    }
  };

  return (
    <div ref={containerRef} className="international-standard bg-[var(--color-background)] text-[var(--color-text)] min-h-screen font-[var(--font-body)] selection:bg-[var(--color-primary)] selection:text-white overflow-x-hidden">
      
      {/* Top Banner */}
      <div className="bg-[var(--color-primary)] text-white py-2 px-6 text-[11px] font-bold uppercase tracking-widest hidden md:block">
          <div className="container mx-auto flex justify-between">
              <span>{schoolName} // International Standards of Excellence</span>
              <div className="flex gap-6">
                  <a href="https://sonoma.school/guardian-login" className="hover:text-[var(--color-accent)]">Portal</a>
                  <a href="/webmail" className="hover:text-[var(--color-accent)]">Webmail</a>
              </div>
          </div>
      </div>

      {/* Corporate Header */}
      <header className="sticky top-0 w-full z-50 bg-white/95 backdrop-blur-md border-b-[3px] border-[var(--color-primary)] shadow-sm">
          <div className="container mx-auto px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-4 group cursor-pointer">
                   <VisualImageEditable 
                      src={previewData?.logoUrl || ""} 
                      alt="Logo" 
                      containerClassName="w-14 h-14"
                      className="w-full h-full object-contain"
                      onImageChange={(u) => handleUpdate('logoUrl', u)}
                      isEditMode={!!isEditMode}
                      fallback={<Shield className="text-[var(--color-primary)] w-full h-full" />}
                  />
                  <div>
                      <h1 className="text-xl md:text-2xl font-black text-[var(--color-primary)] leading-[0.85] font-[var(--font-heading)]">
                          <VisualTextEditable value={schoolName} onSave={(v) => handleUpdate('name', v)} isEditMode={!!isEditMode} />
                      </h1>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Excellence Integrity Service</div>
                  </div>
              </div>
              
              <div className="hidden lg:flex items-center gap-10">
                  <nav className="flex gap-8 text-[11px] font-black uppercase tracking-widest text-gray-600">
                      {mockData.nav.filter(n => n.visible).map((n, i) => (
                          <a key={i} href={n.href} className="hover:text-[var(--color-primary)] transition-colors relative after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[var(--color-primary)] hover:after:w-full after:transition-all">{n.label}</a>
                      ))}
                  </nav>
                  <Button className="bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90 text-white rounded-none px-8 font-bold text-xs h-12 uppercase tracking-widest">Inquire</Button>
              </div>

              <button className="lg:hidden text-[var(--color-primary)]" onClick={() => setMenuOpen(!menuOpen)}>
                  {menuOpen ? <X size={32} /> : <Menu size={32} />}
              </button>
          </div>
      </header>

      {/* Hero Section - Official Carousel Style */}
      {mockData.sectionVisibility.hero && (
      <section className="relative h-[650px] flex items-center overflow-hidden">
          <VisualImageEditable 
              src={mockData.hero.backgroundImage} 
              className="absolute inset-0 w-full h-full object-cover scale-105" 
              alt="Hero"
              onImageChange={(u) => handleUpdate('hero.backgroundImage', u)}
              isEditMode={!!isEditMode}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-transparent" />
          
          <div className="container mx-auto px-6 relative z-10 text-white">
              <span className="inline-block px-4 py-1.5 bg-[var(--color-accent)] text-black text-[11px] font-black uppercase tracking-[0.2em] mb-6">
                <VisualTextEditable value={mockData.hero.tagline} onSave={(v) => handleUpdate('hero.tagline', v)} isEditMode={!!isEditMode} />
              </span>
              <h2 className="text-5xl md:text-8xl font-black font-[var(--font-heading)] leading-[1.05] mb-8 max-w-4xl tracking-tighter">
                  <span className="text-[var(--color-accent)] italic font-normal">
                      <VisualTextEditable value={mockData.hero.titlePrefix} onSave={(v) => handleUpdate('hero.titlePrefix', v)} isEditMode={!!isEditMode} />
                  </span> Academics for a Digital Era.
              </h2>
              <p className="text-xl md:text-2xl font-light max-w-2xl mb-12 opacity-90 border-l-4 border-[var(--color-accent)] pl-8 italic">
                  <VisualTextEditable value={mockData.hero.subtitle} onSave={(v) => handleUpdate('hero.subtitle', v)} isEditMode={!!isEditMode} multiline />
              </p>
              <div className="flex flex-wrap gap-6">
                  <Button size="lg" className="bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/80 text-white rounded-none px-12 h-16 font-black uppercase tracking-widest text-xs">Admissions</Button>
                  <button className="border-2 border-white/30 px-12 h-16 text-white font-black uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-all">School Tour</button>
              </div>
          </div>
      </section>
      )}

      {/* Floating Info Section */}
      <section className="relative z-20 container mx-auto px-6 -mt-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 shadow-2xl">
              {mockData.features.map((item, i) => (
                  <div key={i} className={`p-10 group relative overflow-hidden transition-all duration-500 ${i === 0 ? 'bg-white text-gray-800' : i === 1 ? 'bg-[var(--color-primary)] text-white' : 'bg-gray-800 text-white'}`}>
                      <div className="absolute right-[-10px] bottom-[-10px] text-[8rem] font-black opacity-5 pointer-events-none">0{i+1}</div>
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-6 transition-transform group-hover:scale-110 ${i === 0 ? 'bg-blue-50 text-[var(--color-primary)]' : 'bg-white/10'}`}>
                          {React.createElement(item.icon, { size: 28 })}
                      </div>
                      <h3 className="text-2xl font-black font-[var(--font-heading)] mb-4 italic tracking-tight uppercase">
                        <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`features[${i}].title`, v)} isEditMode={!!isEditMode} />
                      </h3>
                      <p className="text-sm opacity-70 leading-relaxed font-medium mb-8">
                        <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`features[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                      </p>
                      <a href="#" className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest hover:gap-4 transition-all">View Details <ArrowRight size={16}/></a>
                      {isEditMode && <button onClick={() => removeArrayItem('features', i)} className="absolute top-4 right-4 text-[9px] font-bold opacity-30 hover:opacity-100">Drop</button>}
                  </div>
              ))}
          </div>
      </section>

      {/* Stats - Grid Strip */}
      {mockData.sectionVisibility.stats && (
      <section className="py-24 border-b border-gray-100">
          <div className="container mx-auto px-6 grid grid-cols-2 md:grid-cols-3 gap-12 text-center">
              {mockData.stats.map((s, i) => (
                  <div key={i}>
                      <div className="text-5xl md:text-7xl font-black text-[var(--color-primary)] mb-2 font-[var(--font-heading)]">
                        <VisualTextEditable value={s.value} onSave={(v) => handleUpdate(`stats[${i}].value`, v)} isEditMode={!!isEditMode} />
                      </div>
                      <div className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-400">
                        <VisualTextEditable value={s.label} onSave={(v) => handleUpdate(`stats[${i}].label`, v)} isEditMode={!!isEditMode} />
                      </div>
                  </div>
              ))}
          </div>
      </section>
      )}

      {/* Programs - The Curriculum */}
      {mockData.sectionVisibility.programs && (
      <section id="programs" className="py-40 px-6 container mx-auto">
          <div className="text-center mb-24 max-w-3xl mx-auto">
              <span className="text-[var(--color-primary)] font-black tracking-[0.4em] uppercase text-xs mb-4 block">Our Curriculum Paths</span>
              <h2 className="text-5xl md:text-7xl font-black font-[var(--font-heading)] text-gray-900 leading-[0.9] tracking-tighter italic">Foundations of Leadership.</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
              {mockData.programs.map((p, i) => (
                  <div key={i} className="group cursor-pointer">
                      <div className="relative aspect-video overflow-hidden mb-10 border-b-8 border-[var(--color-primary)]">
                          <VisualImageEditable 
                              src={p.img} alt={p.title}
                              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                              onImageChange={(u) => handleUpdate(`programs[${i}].img`, u)}
                              isEditMode={!!isEditMode}
                          />
                          <div className="absolute top-6 left-6 bg-[var(--color-primary)] text-white px-5 py-2 text-[10px] font-black uppercase tracking-widest">{p.age}</div>
                      </div>
                      <h3 className="text-3xl font-black font-[var(--font-heading)] mb-6 text-gray-900 group-hover:text-[var(--color-primary)] transition-colors italic">
                          <VisualTextEditable value={p.title} onSave={(v) => handleUpdate(`programs[${i}].title`, v)} isEditMode={!!isEditMode} />
                      </h3>
                      <p className="text-lg text-gray-500 font-light leading-relaxed mb-8 max-w-xl">
                          <VisualTextEditable value={p.desc} onSave={(v) => handleUpdate(`programs[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                      </p>
                      <button className="text-xs font-black uppercase tracking-widest flex items-center gap-4 group-hover:gap-6 transition-all">Syllabus Details <ArrowRight size={18} className="text-[var(--color-primary)]"/></button>
                      {isEditMode && <button onClick={() => removeArrayItem('programs', i)} className="text-red-500 font-black text-[9px] mt-6 uppercase">Erase Module</button>}
                  </div>
              ))}
              {isEditMode && (
                  <div onClick={() => addArrayItem('programs', { title: "New Module", age: "Years", desc: "Long formal description." })} className="border-4 border-dashed border-gray-100 flex flex-col items-center justify-center p-20 hover:border-[var(--color-primary)] transition-all cursor-pointer text-gray-300 hover:text-[var(--color-primary)]">
                      <Plus size={64}/>
                      <span className="font-black uppercase tracking-widest mt-4">Append Pathway</span>
                  </div>
              )}
          </div>
      </section>
      )}

      {/* Team - The Faculty */}
      {mockData.sectionVisibility.team && (
      <section id="team" className="py-40 bg-gray-50 px-6">
          <div className="container mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-end gap-12 mb-32 border-b-2 border-gray-200 pb-12">
                  <h2 className="text-6xl md:text-8xl font-black font-[var(--font-heading)] leading-none -tracking-widest italic uppercase">Management.</h2>
                  <p className="text-gray-500 max-w-sm italic">Our faculty members are recruited globally for their academic pedigree and passion for mentoring.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                  {mockData.team.map((m, i) => (
                      <div key={i} className="group">
                          <div className="aspect-[4/5] overflow-hidden mb-8 shadow-xl relative scale-100 group-hover:scale-105 transition-all duration-500">
                               <VisualImageEditable 
                                  src={m.img} 
                                  alt={m.name} 
                                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700" 
                                  onImageChange={(u) => handleUpdate(`team[${i}].img`, u)} 
                                  isEditMode={!!isEditMode}
                              />
                               <div className="absolute inset-0 border-[10px] border-white/0 group-hover:border-white/20 transition-all" />
                          </div>
                          <h3 className="text-2xl font-black font-[var(--font-heading)] text-gray-900 mb-1 italic">
                              <VisualTextEditable value={m.name} onSave={(v) => handleUpdate(`team[${i}].name`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[var(--color-primary)]">
                              <VisualTextEditable value={m.role} onSave={(v) => handleUpdate(`team[${i}].role`, v)} isEditMode={!!isEditMode} />
                          </p>
                           {isEditMode && <button onClick={() => removeArrayItem('team', i)} className="text-red-500 font-bold uppercase text-[9px] mt-4">Retire</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('team', { name: "New Leader", role: "Specialist", img: "" })} className="aspect-[4/5] border-4 border-dashed border-gray-200 flex flex-col items-center justify-center p-12 hover:border-[var(--color-primary)] transition-all cursor-pointer text-gray-300">
                          <Plus size={48} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* Gallery - Standard Matrix */}
      {mockData.sectionVisibility.gallery && (
      <section id="gallery" className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 border-y border-gray-100">
          {mockData.gallery.map((item, i) => (
              <div key={i} className="relative aspect-square group overflow-hidden border-r border-gray-100">
                  <VisualImageEditable 
                      src={item.img} alt={item.title}
                      className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-1000"
                      onImageChange={(u) => handleUpdate(`gallery[${i}].img`, u)}
                      isEditMode={!!isEditMode}
                  />
                  <div className="absolute inset-0 bg-[var(--color-primary)]/80 opacity-0 group-hover:opacity-100 transition-opacity p-6 flex flex-col justify-end text-white">
                      <p className="text-[9px] font-black uppercase tracking-widest mb-1">{item.category}</p>
                      <h4 className="text-xl font-black italic font-[var(--font-heading)] leading-none">{item.title}</h4>
                  </div>
              </div>
          ))}
          {isEditMode && (
              <div onClick={() => addArrayItem('gallery', { category: "Campus", title: "Archive", img: "" })} className="aspect-square flex items-center justify-center border-r-2 border-gray-100 cursor-pointer hover:bg-gray-100">
                  <Camera size={32} className="text-gray-300" />
              </div>
          )}
      </section>
      )}

      {/* News - The Chronicle */}
      {mockData.sectionVisibility.news && (
      <section className="py-40 px-6 bg-white">
          <div className="container mx-auto">
              <div className="flex flex-col md:flex-row justify-between items-baseline mb-24 gap-8">
                   <h2 className="text-6xl md:text-9xl font-black font-[var(--font-heading)] -tracking-widest uppercase italic">Chronicle.</h2>
                   <Button variant="outline" className="border-[var(--color-primary)] text-[var(--color-primary)] rounded-none px-8 font-black uppercase tracking-widest text-xs h-12">View Archives</Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-16 border-t-4 border-gray-900 pt-16">
                  {mockData.news.map((item, i) => (
                      <div key={i} className="group">
                          <div className="text-[11px] font-black text-[var(--color-primary)] uppercase tracking-widest mb-6 border-b border-gray-100 pb-2">ISSUE_{item.date}</div>
                          <h3 className="text-3xl font-black font-[var(--font-heading)] text-gray-900 mb-8 leading-[0.9] italic">
                            <VisualTextEditable value={item.title} onSave={(v) => handleUpdate(`news[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h3>
                          <div className="h-60 overflow-hidden mb-8 border-l-[12px] border-gray-100 group-hover:border-[var(--color-primary)] transition-all">
                               <VisualImageEditable 
                                  src={item.img} alt={item.title}
                                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                                  onImageChange={(u) => handleUpdate(`news[${i}].img`, u)}
                                  isEditMode={!!isEditMode}
                              />
                          </div>
                          <p className="text-gray-500 font-medium leading-relaxed mb-8 line-clamp-3 italic">
                              <VisualTextEditable value={item.desc} onSave={(v) => handleUpdate(`news[${i}].desc`, v)} isEditMode={!!isEditMode} multiline />
                          </p>
                          <ArrowRight className="text-[var(--color-primary)] group-hover:translate-x-4 transition-transform" />
                      </div>
                  ))}
              </div>
          </div>
      </section>
      )}

      {/* Downloads - Institutional Matrix */}
      {/* Downloads / Institutional Archives */}
      {mockData.sectionVisibility.downloads && (
      <section className="py-24 bg-gray-50 border-y border-gray-100 px-6">
          <div className="container mx-auto">
              <div className="flex items-center gap-6 mb-16">
                   <div className="h-px flex-1 bg-gray-200" />
                   <h2 className="text-sm font-black uppercase tracking-[0.5em] text-gray-400">
                       <VisualTextEditable 
                           value={mockData.labels.downloadsSection} 
                           onSave={(v) => handleUpdate('labels.downloadsSection', v)} 
                           isEditMode={!!isEditMode} 
                           as="span" 
                       />
                   </h2>
                   <div className="h-px flex-1 bg-gray-200" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {mockData.downloads.map((doc, i) => (
                      <div key={i} className="bg-white p-8 group border-b-4 border-gray-100 hover:border-[var(--color-primary)] transition-all shadow-sm relative">
                          <FileText size={40} className="text-gray-200 mb-6 group-hover:text-[var(--color-primary)] transition-colors" />
                          <h4 className="text-sm font-black uppercase tracking-tight text-gray-900 mb-2 truncate">
                            <VisualTextEditable value={doc.title} onSave={(v) => handleUpdate(`downloads[${i}].title`, v)} isEditMode={!!isEditMode} />
                          </h4>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-4 italic">
                              <VisualTextEditable value={doc.type} onSave={(v) => handleUpdate(`downloads[${i}].type`, v)} isEditMode={!!isEditMode} as="span" /> 
                              <span className="w-1 h-1 bg-gray-200 rounded-full"/> 
                              <VisualTextEditable value={doc.size} onSave={(v) => handleUpdate(`downloads[${i}].size`, v)} isEditMode={!!isEditMode} as="span" />
                          </p>
                          <div className="mt-8 flex justify-between items-center">
                               <button className="text-[10px] font-black text-[var(--color-primary)] uppercase tracking-widest hover:underline">Download.sys</button>
                               <Download size={18} className="text-gray-200" />
                          </div>
                          {isEditMode && <button onClick={() => removeArrayItem('downloads', i)} className="absolute top-4 right-4 text-[9px] font-bold text-red-500 opacity-20 hover:opacity-100 uppercase">Revoke</button>}
                      </div>
                  ))}
                  {isEditMode && (
                      <div onClick={() => addArrayItem('downloads', { title: "NEW_CIRCULAR.pdf", size: "1.0 MB", type: "PDF" })} className="p-8 border-4 border-dashed border-gray-100 flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-all text-gray-200">
                          <Plus size={32} />
                      </div>
                  )}
              </div>
          </div>
      </section>
      )}

      {/* CTA - Global Application */}
      {mockData.sectionVisibility.cta && (
      <section className="py-48 relative bg-gray-900 text-white text-center px-6 overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]" />
          <div className="container mx-auto relative z-10 max-w-5xl">
              <span className="text-[var(--color-accent)] font-black tracking-[0.5em] uppercase text-xs mb-10 block">Commence Professional Journey</span>
              <h2 className="text-6xl md:text-[8rem] font-black font-[var(--font-heading)] leading-none italic mb-16 shadow-2xl">
                 Secure Your <br/> <span className="text-[var(--color-accent)] underline underline-offset-[16px]">Future.</span>
              </h2>
              <div className="flex flex-col sm:flex-row justify-center gap-10">
                   <Button className="bg-[var(--color-primary)] text-white rounded-none h-20 px-16 font-black uppercase tracking-widest text-xs hover:scale-105 transition-all">
                       Submit Application
                   </Button>
                   <Button variant="outline" className="border-white text-white rounded-none h-20 px-16 font-black uppercase tracking-widest text-xs hover:bg-white hover:text-black transition-all">
                       Download Prospectus
                   </Button>
              </div>
          </div>
      </section>
      )}

      {/* Footer - The Hub */}
      <footer id="contact" className="bg-white text-gray-900 pt-32 pb-16 px-6 border-t-[8px] border-gray-900">
          <div className="container mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-24 mb-32 items-start">
                  <div className="lg:col-span-5">
                      <div className="flex items-center gap-4 mb-8">
                           <Shield size={48} className="text-[var(--color-primary)]" />
                           <h2 className="text-4xl font-black font-[var(--font-heading)] italic uppercase">{schoolName}.</h2>
                      </div>
                      <p className="text-lg font-light leading-relaxed mb-12 italic text-gray-400 border-l-4 border-[var(--color-primary)] pl-10 max-w-sm">"Nurturing the global leaders of tomorrow through a tradition of academic rigor and moral integrity."</p>
                      <div className="flex gap-4">
                          {[Facebook, Instagram, Youtube, Linkedin].map((Icon, i) => (
                              <a key={i} href="#" className="w-12 h-12 bg-gray-50 flex items-center justify-center hover:bg-[var(--color-primary)] hover:text-white transition-all shadow-sm"><Icon size={20} /></a>
                          ))}
                      </div>
                  </div>
                  
                  <div className="lg:col-span-2">
                      <h4 className="text-[11px] font-black tracking-[0.4em] uppercase text-[var(--color-primary)] mb-12">DIRECTORY</h4>
                      <ul className="space-y-6 text-sm font-black uppercase">
                          {mockData.nav.map((item, i) => (
                              <li key={i}><a href={item.href} className="hover:line-through transition-all flex items-center gap-6"><span>0{i+1}</span> {item.label}</a></li>
                          ))}
                      </ul>
                  </div>

                  <div className="lg:col-span-3">
                      <h4 className="text-[11px] font-black tracking-[0.4em] uppercase text-[var(--color-primary)] mb-12">COORDINATES</h4>
                      <div className="space-y-12">
                          <div className="flex gap-8 group">
                              <MapPin size={28} className="text-gray-200 shrink-0 group-hover:text-[var(--color-primary)] transition-colors" />
                              <p className="text-sm font-bold leading-tight">
                                  <VisualTextEditable value={mockData.contact.address} onSave={(v) => handleUpdate('contact.address', v)} isEditMode={!!isEditMode} multiline />
                              </p>
                          </div>
                          <div className="flex gap-8 group">
                              <Phone size={28} className="text-gray-200 shrink-0 group-hover:text-[var(--color-primary)] transition-colors" />
                              <p className="text-sm font-bold">
                                  <VisualTextEditable value={mockData.contact.phone} onSave={(v) => handleUpdate('contact.phone', v)} isEditMode={!!isEditMode} />
                              </p>
                          </div>
                      </div>
                  </div>

                  <div className="lg:col-span-2">
                      <h4 className="text-[11px] font-black tracking-[0.4em] uppercase text-[var(--color-primary)] mb-12">COMMUNIQUÉ</h4>
                      <div className="space-y-8">
                           <p className="text-xs text-gray-400 font-bold uppercase tracking-widest italic">Subscribe for official circulars.</p>
                           <div className="flex gap-2 border-b-2 border-gray-100 pb-4 hover:border-[var(--color-primary)] transition-all">
                                <input type="email" placeholder="official@domain.com" className="bg-transparent border-none focus:ring-0 text-sm font-bold w-full placeholder:text-gray-300" />
                                <button className="text-[var(--color-primary)] hover:scale-110 transition-transform"><Send size={24} /></button>
                           </div>
                      </div>
                  </div>
              </div>
              
              <div className="pt-20 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-8 text-[10px] font-black uppercase tracking-[0.4em] text-gray-300">
                  <p>&copy; {new Date().getFullYear()} {schoolName} // ISO_CERTIFIED // SECURE_HUB</p>
                  <div className="flex gap-12 italic underline underline-offset-4">
                      <a href="#" className="hover:text-gray-900">Ethics Charter</a>
                      <a href="#" className="hover:text-gray-900">Privacy Protocol</a>
                  </div>
              </div>
          </div>
      </footer>

      {/* Mobile Menu */}
      {menuOpen && (
          <div className="fixed inset-0 z-[100] bg-white p-12 flex flex-col animate-in fade-in slide-in-from-right duration-500 overflow-hidden">
               <div className="flex justify-between items-center mb-24 border-b border-gray-100 pb-8">
                   <h2 className="text-4xl font-black italic tracking-tighter leading-none text-[var(--color-primary)]">{schoolName}</h2>
                   <button onClick={() => setMenuOpen(false)} className="text-gray-900"><X size={48} /></button>
               </div>
               <nav className="flex flex-col gap-10">
                   {mockData.nav.filter(n => n.visible).map((n, i) => (
                       <a key={i} href={n.href} onClick={() => setMenuOpen(false)} className="text-6xl font-black uppercase tracking-tighter italic text-gray-900 hover:text-[var(--color-primary)] transition-all">
                           {n.label}
                       </a>
                   ))}
               </nav>
               <div className="mt-auto pt-20">
                    <Button className="w-full bg-[var(--color-primary)] text-white h-20 rounded-none font-black text-xs uppercase tracking-widest italic">Access Student Portal</Button>
               </div>
          </div>
      )}
    </div>
  );
};

export default InternationalStandard;
